/**
 * Deploy tier + release for business-web observability — ONE source.
 *
 * Both the Sentry/PostHog init (`observability.ts`) and the auth-context sync
 * (`core/components/ObservabilityAuthSync.tsx`) read these, so they can never
 * disagree about which tier an event belongs to.
 *
 * The tier is resolved by `@tonnie/observability`: `VITE_ENVIRONMENT` first
 * (the container's /env.js — one image runs in every tier), then the bundler's
 * own PROD flag. `NODE_ENV` is deliberately not part of the
 * chain (W0.3 · F16 / G12) — it says `production` in every build container. An
 * unknown tier throws at boot instead of mislabelling every event.
 */

import { resolveObservabilityEnvironment } from '@tonnie/observability';
import type { ObservabilityEnvironment } from '@tonnie/observability';

import { runtimeEnv } from '@/core/config/runtime-env';

// The build SHA — a property of the image, baked by vite.config.ts `define`.
declare const __BUILD_SHA__: string;

export const OBSERVABILITY_ENV: ObservabilityEnvironment = resolveObservabilityEnvironment({
  VITE_ENVIRONMENT: runtimeEnv.get('VITE_ENVIRONMENT'),
  derived: import.meta.env.PROD ? 'production' : 'development',
});

/**
 * The Sentry release is the image's build SHA — the same id the backends
 * report as SENTRY_RELEASE. It names the artefact, so it stays baked.
 */
export const OBSERVABILITY_RELEASE: string =
  typeof __BUILD_SHA__ !== 'undefined' ? __BUILD_SHA__ : 'dev';
